import { useEffect, useState } from 'react';
import { Check, Plus, Trash2 } from 'lucide-react';
import { LABEL_COLORS, type BoardSnapshot, type Label, type LabelColor } from '@kaba/shared';
import { api } from '../../lib/api';
import { useBoardAction } from '../../lib/board';
import { withFormConfirm } from '../../lib/formDeps';
import { Button, cx, IconButton, LABEL_BG, LABEL_NAMES, Sheet } from '../ui/ui';

const inputCls = 'min-h-11 w-full min-w-0 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base sm:min-h-10 sm:text-sm';

/**
 * Labels eines Boards verwalten (0.53): umbenennen, Farbe ändern, löschen, neu anlegen – auch Labels aus
 * Vorlagen. Erreichbar über das Board-Menü und unten in der Labelauswahl einer Karte.
 */
export function LabelsDialog({ open, snapshot, onClose }: { open: boolean; snapshot: BoardSnapshot; onClose: () => void }) {
  const boardId = snapshot.board.id;
  const action = useBoardAction(boardId);
  const used = (id: string) => snapshot.cards.filter((c) => c.labelIds.includes(id)).length;
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<LabelColor>('blue');
  useEffect(() => {
    if (open) {
      setNewName('');
      setNewColor('blue');
    }
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Labels" side="center">
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm text-[var(--ink-soft)]">Änderungen gelten sofort für alle Karten dieses Boards.</p>
        {snapshot.labels.length ? (
          <ul className="flex flex-col gap-2" aria-label="Vorhandene Labels">
            {snapshot.labels.map((l) => (
              <LabelRow
                key={l.id}
                label={l}
                count={used(l.id)}
                onSave={(patch) => action.mutate(() => api.patch(`/api/labels/${l.id}`, patch))}
                onDelete={() => {
                  const n = used(l.id);
                  const name = l.name || LABEL_NAMES[l.color];
                  if (!window.confirm(`Label „${name}“ löschen?${n ? ` Es wird von ${n === 1 ? 'einer Karte' : `${n} Karten`} entfernt.` : ''}`)) return;
                  action.mutate(() => withFormConfirm((q) => api.del(`/api/labels/${l.id}${q}`)));
                }}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm">Dieses Board hat noch keine Labels.</p>
        )}
        <form
          className="flex flex-col gap-2 rounded-xl border border-[var(--line)] p-3"
          onSubmit={(e) => {
            e.preventDefault();
            const name = newName.trim();
            action.mutate(() => api.post(`/api/boards/${boardId}/labels`, { name, color: newColor }));
            setNewName('');
          }}
        >
          <span className="text-sm font-semibold">Neues Label</span>
          <input className={inputCls} aria-label="Name des neuen Labels" placeholder="Name (optional)" maxLength={40} value={newName} onChange={(e) => setNewName(e.target.value)} />
          <Palette value={newColor} onChange={setNewColor} label="Farbe des neuen Labels" />
          <div>
            <Button type="submit" variant="primary">
              <Plus size={16} aria-hidden /> Anlegen
            </Button>
          </div>
        </form>
      </div>
    </Sheet>
  );
}

function LabelRow({ label, count, onSave, onDelete }: { label: Label; count: number; onSave: (patch: { name?: string; color?: LabelColor }) => void; onDelete: () => void }) {
  const [name, setName] = useState(label.name);
  const [colors, setColors] = useState(false);
  // Änderungen anderer übernehmen, solange hier nicht getippt wird
  useEffect(() => setName(label.name), [label.name]);
  const shown = label.name || LABEL_NAMES[label.color];
  const commit = () => {
    const n = name.trim();
    if (n !== label.name) onSave({ name: n });
  };
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-[var(--line)] p-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={`Farbe von „${shown}“: ${LABEL_NAMES[label.color]} – ändern`}
          aria-expanded={colors}
          onClick={() => setColors((x) => !x)}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg hover:bg-black/5 sm:size-10 dark:hover:bg-white/10"
        >
          <span className={cx('h-4 w-8 rounded-full', LABEL_BG[label.color])} aria-hidden />
        </button>
        <input
          className={inputCls}
          aria-label={`Name von „${shown}“`}
          placeholder={LABEL_NAMES[label.color]}
          maxLength={40}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape' && name !== label.name) {
              e.stopPropagation();
              setName(label.name);
            }
          }}
        />
        <span className="hidden shrink-0 text-xs text-[var(--ink-soft)] sm:inline">{count === 1 ? '1 Karte' : `${count} Karten`}</span>
        <IconButton label={`„${shown}“ löschen`} onClick={onDelete} className="size-11 shrink-0 sm:size-10">
          <Trash2 size={17} />
        </IconButton>
      </div>
      {colors && (
        <Palette
          value={label.color}
          label={`Farbe von „${shown}“`}
          onChange={(c) => {
            setColors(false);
            if (c !== label.color) onSave({ color: c });
          }}
        />
      )}
    </li>
  );
}

function Palette({ value, onChange, label }: { value: LabelColor; onChange: (c: LabelColor) => void; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-1.5">
      {LABEL_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={c === value}
          aria-label={LABEL_NAMES[c]}
          title={LABEL_NAMES[c]}
          onClick={() => onChange(c)}
          className={cx('inline-flex h-9 w-11 items-center justify-center rounded-md text-white', LABEL_BG[c], c === value && 'ring-2 ring-[var(--ink)] ring-offset-2 ring-offset-[var(--panel)]')}
        >
          {c === value && <Check size={16} aria-hidden />}
        </button>
      ))}
    </div>
  );
}
